// LAP — Agrégation des sources Life du « Conseiller en Organisation ».
//
// Même approche que `fetchTimeAdvisorSources` (ConseillerTemps) : chaque
// requête est isolée (catch indépendant) pour qu'un endpoint absent ne fasse
// pas tomber tout l'agrégat — `buildOrganisationAdvisor` produit alors un
// diagnostic partiel mais exploitable.
//
// On utilise `task-operations` (et non `getTasks`) car ce dernier EXCLUT les
// tâches terminées : or le taux de complétion en a besoin.
import { makeGetRequest } from 'utils/api'
import urls from 'utils/urls'

/**
 * Normalise une réponse API en tableau, quelle que soit l'enveloppe
 * (array nu, { results: [...] }, { data: [...] }).
 */
export function extractList(payload) {
  if (!payload) return []
  if (Array.isArray(payload)) return payload
  if (Array.isArray(payload.results)) return payload.results
  if (Array.isArray(payload.data)) return payload.data
  return []
}

/**
 * Récupère toutes les sources nécessaires à `buildOrganisationAdvisor`.
 *
 * @param {object} fallbackUser - profil Redux à utiliser si /api/user/user/
 *   échoue (évite un agrégat sans utilisateur).
 * @returns {Promise<object>} agrégat prêt à passer à buildOrganisationAdvisor.
 */
export async function fetchOrganisationAdvisorSources(fallbackUser = {}) {
  const safeGet = async (path) => {
    try {
      const r = await makeGetRequest(path, {}, true)
      return r?.data ?? null
    } catch (_) {
      return null
    }
  }

  const [userData, tasks, projects, listes, objectifs, domains] = await Promise.all([
    safeGet('/api/user/user/'),
    // task-operations : TOUTES les tâches de l'utilisateur (terminées comprises).
    safeGet(urls.taskOperations),
    safeGet(urls.projectOperations),
    safeGet(urls.listeOperations),
    safeGet(urls.crudMesObjectifs),
    safeGet(urls.getDomain),
  ])

  return {
    user: userData || fallbackUser || {},
    tasks: extractList(tasks),
    projects: extractList(projects),
    listes: extractList(listes),
    objectifs: extractList(objectifs),
    domains: extractList(domains),
  }
}

export default fetchOrganisationAdvisorSources
